import { useEffect, useState } from "react";
import { FileCheck2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

// Pestaña de Configuración de la entidad: qué documentos y datos son obligatorios para
// contratar cada tipo de producto de crédito. El backend ya existe (GET/PUT /requisitos-credito)
// y no se toca aquí: esta pantalla solo lee y escribe las banderas `obligatorio`.
export default function RequisitosPorProducto({ puedeEditar }) {
  const [configs, setConfigs] = useState(null);
  const [guardando, setGuardando] = useState("");

  useEffect(() => {
    api.get("/requisitos-credito")
      .then((r) => setConfigs(r.data))
      .catch(() => setConfigs([]));
  }, []);

  const toggle = (clave, grupo, idx) => {
    setConfigs((prev) => prev.map((c) => {
      if (c.clave !== clave) return c;
      const lista = [...(c[grupo] || [])];
      lista[idx] = { ...lista[idx], obligatorio: !lista[idx].obligatorio };
      return { ...c, [grupo]: lista };
    }));
  };

  const guardar = async (cfg) => {
    setGuardando(cfg.clave);
    try {
      const { data } = await api.put(`/requisitos-credito/${cfg.clave}`, {
        documentos: cfg.documentos, campos_datos: cfg.campos_datos,
      });
      setConfigs((prev) => prev.map((c) => (c.clave === cfg.clave ? data : c)));
      toast.success("Requisitos guardados");
    } catch (e) { toast.error(apiError(e)); }
    finally { setGuardando(""); }
  };

  if (!configs) return null;

  return (
    <div className="space-y-4" data-testid="requisitos-producto">
      <p className="text-xs text-slate-500">
        Marca qué documentos y datos son obligatorios para contratar cada tipo de producto. Lo
        opcional se puede subir si el cliente lo tiene, pero no detiene la contratación.
      </p>
      {configs.map((cfg) => (
        <div key={cfg.clave} className="rounded-lg border border-slate-200 bg-white p-5">
          <h4 className="mb-3 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700">
            <FileCheck2 className="h-4 w-4 text-forest-600" /> {cfg.tipo_label}
          </h4>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Documentos</p>
              <div className="space-y-2">
                {(cfg.documentos || []).map((d, i) => (
                  <div key={d.clave} className="flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
                    <span className="text-sm text-slate-700">{d.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-slate-400">Obligatorio</span>
                      <Switch data-testid={`req-${cfg.clave}-doc-${d.clave}`} checked={!!d.obligatorio}
                        onCheckedChange={() => toggle(cfg.clave, "documentos", i)} disabled={!puedeEditar} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Datos del crédito</p>
              <div className="space-y-2">
                {(cfg.campos_datos || []).map((c, i) => (
                  <div key={c.clave} className="flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
                    <span className="text-sm text-slate-700">{c.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-slate-400">Obligatorio</span>
                      <Switch data-testid={`req-${cfg.clave}-campo-${c.clave}`} checked={!!c.obligatorio}
                        onCheckedChange={() => toggle(cfg.clave, "campos_datos", i)} disabled={!puedeEditar} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {puedeEditar && (
            <div className="mt-4 flex justify-end">
              <Button size="sm" data-testid={`req-${cfg.clave}-guardar`}
                onClick={() => guardar(cfg)} disabled={guardando === cfg.clave}>
                {guardando === cfg.clave ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
